<template>
  <div class="min-h-screen flex flex-col bg-white text-fg font-sans">
    <!-- Header -->
    <header
      data-testid="header"
      class="fixed top-0 left-0 right-0 z-40 pt-safe backdrop-blur-xl backdrop-saturate-150 border-b transition-all duration-300 bg-white/85 border-line/60 shadow-sm"
    >
      <div class="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <router-link
          data-testid="link-logo"
          class="flex items-center gap-2 cursor-pointer"
          to="/"
        >
          <img src="/images/tble_logo2.svg" alt="티블비즈" class="h-7 w-auto" />
        </router-link>

        <nav class="hidden md:flex items-center gap-6">
          <button
            data-testid="nav-서비스"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('services')"
          >
            서비스
          </button>
          <button
            data-testid="nav-혜택"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('benefits')"
          >
            혜택
          </button>
          <button
            data-testid="nav-성과"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('performance')"
          >
            성과
          </button>
          <button
            data-testid="nav-진행 방식"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('process')"
          >
            진행 방식
          </button>
          <button
            data-testid="nav-FAQ"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('faq')"
          >
            FAQ
          </button>
        </nav>

        <div class="flex items-center gap-3">
          <button
            data-testid="button-header-cta"
            class="hidden md:inline-flex items-center px-5 py-2.5 rounded-[12px] border text-sm font-semibold backdrop-blur-sm transition-colors cursor-pointer border-line bg-white/60 text-fg hover:bg-white"
            @click="openModal"
          >
            무료 상담 신청
          </button>
          <button
            data-testid="button-mobile-menu"
            class="md:hidden -mr-2 flex items-center justify-center min-w-[44px] min-h-[44px] transition-colors cursor-pointer text-fg"
            aria-label="메뉴 열기"
            @click="isMobileMenuOpen = !isMobileMenuOpen"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-menu"
              aria-hidden="true"
            >
              <path d="M4 5h16" />
              <path d="M4 12h16" />
              <path d="M4 19h16" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Nav Drawer -->
      <div v-if="isMobileMenuOpen" class="md:hidden bg-white border-b border-line px-4 py-4 space-y-3 shadow-md">
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('services')">서비스</button>
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('benefits')">혜택</button>
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('performance')">성과</button>
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('process')">진행 방식</button>
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('faq')">FAQ</button>
        <button data-testid="button-header-cta" class="w-full py-3 rounded-xl bg-brand-500 text-white font-bold text-sm" @click="openModal">무료 상담 신청</button>
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex-1">
      <div class="pt-16 pb-24 md:pb-0">
        <!-- Hero Section -->
        <section class="py-16 md:py-24 bg-gradient-to-br from-brand-50 to-white">
          <div class="max-w-7xl mx-auto px-4 md:px-6">
            <span class="text-xs font-bold uppercase tracking-widest text-brand-500 mb-3 block">FAQ</span>
            <h1 class="text-2xl md:text-4xl font-bold text-fg mb-5">자주 묻는 질문</h1>
            <p class="text-base text-fg-muted leading-relaxed max-w-xl">더 궁금한 점은 무료 상담으로 정확히 안내드립니다.</p>
          </div>
        </section>

        <!-- FAQ Categories & List Section -->
        <section class="py-16 bg-white">
          <div class="max-w-4xl mx-auto px-4 md:px-6">
            <!-- Category Filter Tabs -->
            <div class="flex flex-wrap gap-2 mb-10">
              <button
                v-for="cat in categories"
                :key="cat.id"
                :data-testid="'faq-category-' + cat.id"
                class="px-5 py-2.5 rounded-full text-sm font-semibold transition-colors cursor-pointer"
                :class="selectedCat === cat.id ? 'bg-brand-500 text-white' : 'bg-bg-soft border border-line text-fg-muted hover:border-brand-300'"
                @click="selectedCat = cat.id"
              >
                {{ cat.name }}
              </button>
            </div>

            <!-- FAQ Groups -->
            <div v-for="group in filteredFaqGroups" :key="group.id" class="mb-10">
              <h2 class="text-base font-bold text-brand-500 uppercase tracking-widest mb-4">{{ group.name }}</h2>

              <div class="space-y-3">
                <div
                  v-for="(item, idx) in group.items"
                  :key="idx"
                  :data-testid="'faq-page-item-' + group.id + '-' + idx"
                  class="bg-bg-soft rounded-[14px] border border-line overflow-hidden"
                >
                  <button
                    type="button"
                    :data-testid="'faq-page-toggle-' + group.id + '-' + idx"
                    class="w-full flex items-center justify-between px-5 py-4 text-left gap-4 cursor-pointer"
                    @click="toggleItem(group.id, idx)"
                  >
                    <span class="text-base font-semibold text-fg">{{ item.q }}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-chevron-down flex-shrink-0 text-fg-muted transition-transform duration-200"
                      :class="{ 'rotate-180': isExpanded(group.id, idx) }"
                      aria-hidden="true"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>

                  <div
                    v-if="isExpanded(group.id, idx)"
                    class="px-5 pb-5 text-sm text-fg-muted leading-relaxed border-t border-line/40 pt-4 bg-white/50"
                  >
                    {{ item.a }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Bottom CTA Banner -->
            <div class="text-center mt-12 py-10 bg-brand-50 rounded-[20px] border border-brand-100">
              <p class="text-base font-semibold text-fg mb-5">더 궁금한 점은 무료 상담으로 정확히 안내드립니다</p>
              <button
                data-testid="button-faq-page-cta"
                class="px-8 py-4 rounded-[12px] bg-brand-500 text-white font-semibold text-base hover:bg-brand-600 transition-colors cursor-pointer"
                @click="openModal"
              >
                무료 상담 신청하기
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>

    <!-- Footer -->
    <footer class="bg-fg text-white/70 pt-12 pb-28 md:pb-12">
      <div class="max-w-7xl mx-auto px-4 md:px-6">
        <div class="mb-8 flex items-center gap-3">
          <img src="/images/tble_logo2.svg" alt="티블비즈" class="h-7 w-auto" />
        </div>
        <p class="text-sm text-white/50 mb-8 -mt-4">다채널 리뷰 마케팅으로 매출을 올리는 광고주 서비스</p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs leading-relaxed mb-8 text-white/50">
          <div class="space-y-1">
            <p>상호명: 주식회사 쓰리얼라이언스</p>
            <p>대표이사: 성동효</p>
            <p>주소: 서울시 강남구 학동로 44길 16, 1F</p>
            <p>사업자등록번호: 217-81-47577</p>
            <p>통신판매등록번호: 제2025-서울강남-04400호</p>
          </div>
          <div class="space-y-1">
            <p>개인정보 보호 최고책임자: 성동효</p>
            <p>이메일: help@3alliance.co.kr</p>
            <!-- <p>대표전화: 1833-5985</p> -->
            <!-- <p>FAX: 0504-334-6358</p> -->
            <p>상담시간: 10:00 ~ 18:00 · 토·일·공휴일 휴무</p>
          </div>
        </div>

        <div class="border-t border-white/10 pt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-1 md:gap-4">
          <p class="text-xs text-white/40 py-2">© 2026 주식회사 쓰리얼라이언스. All rights reserved.</p>
          <div class="flex gap-4 -my-1">
            <a href="#" data-testid="footer-link-개인정보 처리방침" class="inline-flex items-center min-h-[44px] text-xs text-white/50 hover:text-white transition-colors" @click.prevent>개인정보 처리방침</a>
            <a href="#" data-testid="footer-link-이용약관" class="inline-flex items-center min-h-[44px] text-xs text-white/50 hover:text-white transition-colors" @click.prevent>이용약관</a>
          </div>
        </div>
      </div>
    </footer>

<button
      type="button"
      data-testid="button-floating-contact"
      aria-label="무료 상담 신청"
      class="fixed bottom-6 right-4 md:bottom-8 md:right-6 z-[9998] flex items-center gap-2 pl-4 pr-5 h-14 rounded-full bg-brand-500 text-white font-semibold text-sm shadow-[0_8px_24px_rgba(242,178,42,0.45)] hover:bg-brand-600 hover:shadow-[0_12px_28px_rgba(242,178,42,0.55)] transition-all hover:scale-105 cursor-pointer"
      @click="toggleModal"
    >
      <span class="flex items-center justify-center w-7 h-7 rounded-full bg-white/15">
        <img src="/images/headset.svg" alt="" class="w-4 h-4 brightness-0 invert" />
      </span>
      <span>문의하기</span>
    </button>

    <!-- Consultation Widget Modal -->
    <ConsultationModal :is-open="isModalOpen" @close="isModalOpen = false" />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import ConsultationModal from '../common/ConsultationModal.vue';

const router = useRouter();
const isMobileMenuOpen = ref(false);
const isModalOpen = ref(false);
const selectedCat = ref('all');
const expandedKeys = ref(new Set());

const categories = [
  { id: 'all', name: '전체' },
  { id: 'cost', name: '비용·계약' },
  { id: 'duration', name: '진행 기간' },
  { id: 'prep', name: '준비물·조건' },
  { id: 'results', name: '성과·혜택' },
  { id: 'channels', name: '채널·서비스' },
  { id: 'privacy', name: '개인정보' },
];

const faqGroups = [
  {
    id: 'cost',
    name: '비용·계약',
    items: [
      { q: '비용은 어떻게 정해지나요?', a: '채널 조합·회차·규모에 따라 달라집니다. 상담 시 운영 범위와 견적을 함께 안내드립니다.' },
      { q: '최소 계약 기간이 있나요?', a: '단발성 1회 진행부터 월 단위 연간 계약까지 가능하며 기본 1개월 단위로 계약 진행됩니다.' },
      { q: '중도 해지가 가능한가요?', a: '캠페인 세팅 및 인플루언서 매칭 시작 전인 경우 취소 및 전액 환불이 가능합니다.' },
      { q: '결제 방식은 어떻게 되나요?', a: '세금계산서 발행(계좌이체) 및 신용카드 결제를 지원합니다.' },
    ],
  },
  {
    id: 'duration',
    name: '진행 기간',
    items: [
      { q: '진행 기간은 얼마나 걸리나요?', a: '월 2회 회전이 기본, 첫 발행까지 평균 2~4주입니다. 규모에 따라 일정은 조정됩니다.' },
      { q: '효과가 나타나기까지 얼마나 걸리나요?', a: '체험단 발행 후 1~2주 내 검색 노출 및 유입 변화를 체감하실 수 있습니다.' },
      { q: '한 번에 몇 개 채널을 동시에 운영할 수 있나요?', a: '블로그·인스타·구매평·맵리뷰 등 최대 6개 채널을 한꺼번에 동시 진행 가능합니다.' },
      { q: '캠페인이 끝난 후에도 리뷰가 유지되나요?', a: '작성된 리뷰는 블로그/SNS/스토어에 영구 보존됩니다.' },
    ],
  },
  {
    id: 'prep',
    name: '준비물·조건',
    items: [
      { q: '광고주가 직접 준비해야 하는 것이 있나요?', a: '기본적인 매장 정보, 상품 소구점, 이미지 전달 및 진행 승인만 해주시면 됩니다.' },
      { q: '방문형 체험단 시 제공 내역은 어떻게 설정하나요?', a: '매장 대표 메뉴나 시술권을 체험 상품으로 설정해 주시면 되며, 적정 가액을 상담 시 제안드립니다.' },
      { q: '어떤 업종에서 운영이 가능한가요?', a: '식음료, 뷰티, 패션, 이커머스, 학원, 병원, IT 앱 등 전 분야 운영 가능합니다.' },
      { q: '리뷰 내용을 광고주가 지정할 수 있나요?', a: '강조하고 싶은 핵심 키워드 및 미션 가이드를 지정하실 수 있습니다.' },
    ],
  },
  {
    id: 'results',
    name: '성과·혜택',
    items: [
      { q: '우리 업종에도 효과가 있을까요?', a: '식음·뷰티·이커머스·앱 등 폭넓은 업종을 운영해 왔습니다. 상담 시 업종별 사례를 보여드립니다.' },
      { q: '당일 계약 프로모션은 어떻게 적용되나요?', a: '당일 계약 시 할인가와 숏폼 무상 제공 등 혜택이 적용됩니다. 패키지 구성 계약 기준이며, 조건은 상담 시 안내드립니다.' },
      { q: '성과는 어떻게 확인하나요?', a: '대시보드에서 진행 현황·등록 리뷰·유입·조회수를 실시간 확인하고 종결 보고서를 제공합니다.' },
      { q: '사례에 나온 수치가 내 업종에도 동일하게 나오나요?', a: '수치는 특정 광고주 사례이며 업종·상품·기간에 따라 상이할 수 있습니다.' },
      { q: '성과 기준 미달 시 어떻게 되나요?', a: '가이드 미준수건 발생 시 100% 재작성 및 수정을 티블이 전담 관리합니다.' },
    ],
  },
  {
    id: 'channels',
    name: '채널·서비스',
    items: [
      { q: '어떤 채널을 선택해야 할지 모르겠어요', a: '1:1 전담 매니저가 광고주의 업종과 현 상황을 분석하여 맞춤 채널 조합을 제안합니다.' },
      { q: '블로그와 인스타 중 어떤 것이 더 효과적인가요?', a: '검색 유입이 중요한 경우 블로그, 시각적 확산과 비주얼이 중요한 경우 인스타를 추천합니다.' },
      { q: '기존에 진행 중인 광고와 함께 운영해도 되나요?', a: '기존 퍼포먼스/SA 광고와 병행 시 검토 단계에서의 전환율이 대폭 증가합니다.' },
      { q: '리뷰어 수준은 어떻게 검증하나요?', a: '52만 인플루언서 풀 중 실제 일방문자수 및 영향력 지표를 종합 검수하여 매칭합니다.' },
    ],
  },
  {
    id: 'privacy',
    name: '개인정보',
    items: [
      { q: '입력한 개인정보는 어디에 사용되나요?', a: '1:1 맞춤 상담 신청 및 담당 매니저 안내 목적으로만 사용됩니다.' },
      { q: '개인정보는 얼마나 보관되나요?', a: '상담 완료 후 최대 6개월 보관 후 안전하게 파기됩니다.' },
      { q: '상담 신청 후 스팸 연락이 오지는 않나요?', a: '티블비즈 전담 매니저의 1:1 직통 안내 외 불필요한 마케팅/스팸 연락은 일절 없습니다.' },
      { q: '개인정보 삭제를 요청할 수 있나요?', a: '고객센터 또는 이메일(help@3alliance.co.kr)로 즉시 삭제 요청이 가능합니다.' },
    ],
  },
];

const filteredFaqGroups = computed(() => {
  if (selectedCat.value === 'all') return faqGroups;
  return faqGroups.filter((g) => g.id === selectedCat.value);
});

const isExpanded = (groupId, idx) => {
  return expandedKeys.value.has(`${groupId}-${idx}`);
};

const toggleItem = (groupId, idx) => {
  const key = `${groupId}-${idx}`;
  if (expandedKeys.value.has(key)) {
    expandedKeys.value.delete(key);
  } else {
    expandedKeys.value.add(key);
  }
};

const navToHomeSection = (sectionId) => {
  isMobileMenuOpen.value = false;
  router.push({ path: '/', hash: '#' + sectionId });
};

const openModal = () => {
  isModalOpen.value = true;
};

const toggleModal = () => {
  isModalOpen.value = !isModalOpen.value;
};
</script>
